import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  FaArrowLeft, 
  FaCalendarAlt, 
  FaChevronDown, 
  FaEllipsisH, 
  FaPlay, 
  FaPlus, 
  FaTimes, 
  FaUserPlus 
} from 'react-icons/fa';
import { BsFillPersonFill, BsThreeDotsVertical } from 'react-icons/bs';
import { IoMdClose } from 'react-icons/io';

const RunHistory = () => {
  const { projectId } = useParams();
  const [isAuditLoggingEnabled, setIsAuditLoggingEnabled] = useState(true);
  const [selectedRun, setSelectedRun] = useState(null);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);

  const projects = [
    {
      id: 1,
      name: 'Project Delta',
      date: 'May 05, 2025',
      lastUpdated: 'May 10, 2025',
      models: ['GPT-4', 'Claude'],
      status: 'active',
    },
    {
      id: 2,
      name: 'Project Alpha',
      date: 'May 01, 2025',
      lastUpdated: 'May 7, 2025',
      models: ['GPT-4'],
      status: 'active',
    }
  ];
  
  // REMOVE the runData array and any mock data

  useEffect(() => {
    // Simulate loading project data
    setLoading(true);
    setTimeout(() => {
      const foundProject = projects.find(p => p.id === parseInt(projectId));
      setProject(foundProject || projects[0]); // Default to first project if not found
      setLoading(false);
    }, 500);
  }, [projectId]);

  const getRunsForCurrentProject = () => {
    // This function will need to be implemented to fetch runs from an API
    // For now, it will return an empty array or throw an error if not implemented
    console.error("getRunsForCurrentProject is not yet implemented. Please fetch runs from an API.");
    return [];
  };

  const handleRunSelection = (run) => {
    setSelectedRun(run);
  };

  const getStatusClass = (status) => {
    switch(status) {
      case 'success':
        return 'bg-green-100 text-green-800';
      case 'error':
        return 'bg-red-100 text-red-800';
      case 'warning':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Loading run history...</div>;
  }

  if (!project) {
    return <div className="text-red-500">Project not found</div>;
  }

  return (
    <div className="space-y-6">
      {/* Project Header */}
      <div className="bg-white -m-6 p-6 mb-6 shadow-sm">
        <div className="flex items-center">
          <Link to={`/projects/${projectId}`} className="text-gray-600 flex items-center">
            <FaArrowLeft className="mr-2" /> Back to Project
          </Link>
        </div>
        
        <div className="mt-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold">{project.name} - Run History</h1>
          <button className="bg-blue-600 text-white px-4 py-2 rounded-md flex items-center">
            <FaPlay className="mr-2" /> Test Workflow
          </button>
        </div>
      </div>

      {/* Run History Content */}
      <div className="flex gap-6">
        {/* Left side - Run list */}
        <div className="w-2/3">
          <div className="bg-white rounded-md shadow-sm overflow-hidden">
            <div className="border-b border-gray-200 p-4 flex justify-between items-center">
              <h2 className="text-lg font-semibold">Recent Runs</h2>
              <div className="flex gap-2">
                <button className="flex items-center border border-gray-300 text-gray-600 rounded px-3 py-1 text-sm">
                  <FaCalendarAlt className="mr-2" /> Filter by Date
                </button>
                <button className="flex items-center border border-gray-300 text-gray-600 rounded px-3 py-1 text-sm">
                  <FaChevronDown className="mr-2" /> Status
                </button>
              </div>
            </div>
            
            <div className="divide-y divide-gray-100">
              {getRunsForCurrentProject().length > 0 ? (
                getRunsForCurrentProject().map(run => (
                  <div 
                    key={run.id}
                    onClick={() => handleRunSelection(run)}
                    className={`p-4 hover:bg-gray-50 cursor-pointer ${selectedRun?.id === run.id ? 'bg-blue-50' : ''}`}
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <div className="font-medium">{run.timestamp}</div>
                        <div className="text-sm text-gray-500">
                          Duration: {run.duration} | Tokens: {run.tokensUsed}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`px-2 py-1 rounded-full text-xs ${getStatusClass(run.status)}`}>
                          {run.status}
                        </span>
                        <FaEllipsisH className="text-gray-400" />
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-6 text-center text-gray-500">
                  No run history available for this project.
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right side - Run details */}
        <div className="w-1/3">
          {selectedRun ? (
            <div className="bg-white rounded-md shadow-sm p-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold">Run Details</h3>
                <button onClick={() => setSelectedRun(null)} className="text-gray-500">
                  <IoMdClose />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <div className="text-sm text-gray-500">Timestamp</div>
                  <div>{selectedRun.timestamp}</div>
                </div>
                
                <div>
                  <div className="text-sm text-gray-500">Status</div>
                  <div className={`inline-block px-2 py-1 rounded-full text-xs ${getStatusClass(selectedRun.status)}`}>
                    {selectedRun.status}
                  </div>
                </div>
                
                <div>
                  <div className="text-sm text-gray-500">Duration</div>
                  <div>{selectedRun.duration}</div>
                </div>
                
                <div>
                  <div className="text-sm text-gray-500">Tokens Used</div>
                  <div>{selectedRun.tokensUsed}</div>
                </div>
                
                <div>
                  <div className="text-sm text-gray-500">Model</div>
                  <div>{selectedRun.model}</div>
                </div>
                
                <div>
                  <div className="text-sm text-gray-500">Input Size</div>
                  <div>{selectedRun.inputSize}</div>
                </div>
                
                <div>
                  <div className="text-sm text-gray-500">Output Size</div>
                  <div>{selectedRun.outputSize}</div>
                </div>
                
                <div>
                  <div className="text-sm text-gray-500">Executed By</div>
                  <div>{selectedRun.user}</div>
                </div>
                
                <div className="pt-4 border-t border-gray-100">
                  <button className="w-full bg-blue-600 text-white py-2 rounded-md">
                    View Complete Logs
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-md shadow-sm p-6 text-center text-gray-500">
              Select a run to view details
            </div>
          )}
          
          <div className="bg-white rounded-md shadow-sm p-4 mt-6">
            <h3 className="font-semibold mb-4">Run Settings</h3>
            
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm">Audit Logging</span>
              <label className="switch">
                <input 
                  type="checkbox" 
                  checked={isAuditLoggingEnabled}
                  onChange={() => setIsAuditLoggingEnabled(!isAuditLoggingEnabled)}
                  className="sr-only"
                />
                <div className={`w-10 h-5 rounded-full ${isAuditLoggingEnabled ? 'bg-blue-500' : 'bg-gray-300'}`}>
                  <div className={`h-4 w-4 rounded-full bg-white transform transition-transform ${isAuditLoggingEnabled ? 'translate-x-5' : 'translate-x-1'} mt-0.5`}></div>
                </div>
              </label>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-sm">Auto-archive Runs (7 days)</span>
              <label className="switch">
                <input type="checkbox" className="sr-only" />
                <div className="w-10 h-5 rounded-full bg-gray-300">
                  <div className="h-4 w-4 rounded-full bg-white transform translate-x-1 mt-0.5"></div>
                </div>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RunHistory; 